import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ProblemSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 md:w-96 md:h-96 bg-blue-50 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-64 h-64 md:w-96 md:h-96 bg-emerald-50 rounded-full blur-[100px] animate-pulse"></div>
        {/* Grid Overlay */}
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center opacity-[0.03]"></div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-emerald-50 backdrop-blur-sm rounded-full mb-3 sm:mb-4 md:mb-6 border border-emerald-200 shadow-sm">
            <i className="ri-alert-line text-[#ea580c] text-xs sm:text-sm md:text-base"></i>
            <span className="text-xs sm:text-sm font-medium text-[#ea580c]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('problem.badge')}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#0F172A] mb-3 sm:mb-4 md:mb-6 leading-tight px-2 lg:px-0" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            {t('problem.title')}<br className="hidden md:block" />
            <span className="bg-gradient-to-r from-[#dc2626] to-[#ea580c] bg-clip-text text-transparent animate-pulse"> {t('problem.titleHighlight')}</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed px-2 lg:px-0" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('problem.subtitle')}
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-8 mb-8 sm:mb-12 md:mb-16">
          {[
            { icon: 'ri-time-line', stat: 'problem.problem1.stat', title: 'problem.problem1.title', desc: 'problem.problem1.desc' },
            { icon: 'ri-search-eye-line', stat: 'problem.problem2.stat', title: 'problem.problem2.title', desc: 'problem.problem2.desc' },
            { icon: 'ri-star-line', stat: 'problem.problem3.stat', title: 'problem.problem3.title', desc: 'problem.problem3.desc' },
            { icon: 'ri-emotion-unhappy-line', stat: 'problem.problem4.stat', title: 'problem.problem4.title', desc: 'problem.problem4.desc' },
            { icon: 'ri-share-line', stat: 'problem.problem5.stat', title: 'problem.problem5.title', desc: 'problem.problem5.desc' },
            { icon: 'ri-shield-cross-line', stat: 'problem.problem6.stat', title: 'problem.problem6.title', desc: 'problem.problem6.desc' }
          ].map((item, index) => (
            <div
              key={index}
              className={`group bg-white p-5 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl md:rounded-3xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-lg shadow-sm ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${(index + 1) * 100}ms` }}
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#dc2626] to-[#ea580c] rounded-xl md:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-md">
                <i className={`${item.icon} text-white text-lg sm:text-xl md:text-2xl`}></i>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-[#0F172A] mb-2 sm:mb-3 md:mb-4" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                {t(item.title)}
              </h3>
              <p className="text-xs sm:text-sm md:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {t(item.desc)}
              </p>
              <div className="flex items-center gap-2 text-[#ea580c] text-xs sm:text-sm md:text-base font-medium group-hover:translate-x-1 transition-transform">
                <i className="ri-arrow-right-line"></i>
                <span style={{ fontFamily: 'DM Sans, sans-serif' }}>{t(item.stat)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Impact Stats */}
        <div className={`bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-10 lg:p-12 border border-slate-200 shadow-xl transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="text-center mb-5 sm:mb-6 md:mb-8">
            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0F172A] mb-2 sm:mb-3 md:mb-4 px-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              {t('problem.impact.title')}
            </h3>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto px-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('problem.impact.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {['stat1', 'stat2', 'stat3', 'stat4'].map((stat, i) => (
              <div key={i} className="text-center p-3 sm:p-4 md:p-6 bg-slate-50 rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 hover:border-emerald-300 transition-all duration-300 hover:-translate-y-1">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#ea580c] mb-1 sm:mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                  {t(`problem.impact.${stat}`)}
                </div>
                <p className="text-xs sm:text-sm text-slate-500 leading-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t(`problem.impact.${stat}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
